// ─────────────────────────────────────────────────────────────────────────────
// components/Logo.tsx — THE MARK, ONCE.
//
// T5, "the painted kerb" (Buds, 16 Aug 2026): a tile, the M, and one band of
// marigold along the bottom edge — the painted plinth on a shop wall. Three
// shapes and nothing else.
//
// It lives here as a component and not as five <img> tags because the wordmark
// appears on four surfaces and today's lesson was paid for four times over:
// THREE COPIES OF A THING MEANS TWO OF THEM ARE WRONG. The SVGs in
// public/brand/ are the same geometry, for print and for anyone outside the app.
//
// The kerb is a PATH, not a clipped rect, so an inline logo carries no <clipPath>
// id — two on one page would collide.
// ─────────────────────────────────────────────────────────────────────────────

/** Where the tile's rounded corner has already cut in by the time the kerb
 *  starts: rx 15, band at y=52, so 15 - sqrt(15² - 3²). */
const KERB =
  "M0.303 52 H63.697 A15 15 0 0 1 49 64 H15 A15 15 0 0 1 0.303 52 Z";
const M =
  "M12.5 45.5 V17 h6 L32 34.5 45.5 17 h6 v28.5 h-6.4 V27.6 L33.4 42.4 h-2.8 L18.9 27.6 V45.5 Z";

const INK = "#16213c";
const RANI = "#c2255c";
const MARIGOLD = "#f5a31a";
const PAPER = "#ffffff";

const TONES = {
  ink: { tile: INK, letter: PAPER, kerb: MARIGOLD },
  rani: { tile: RANI, letter: PAPER, kerb: MARIGOLD },
  paper: { tile: PAPER, letter: INK, kerb: MARIGOLD },
} as const;

export type LogoTone = keyof typeof TONES;

/**
 * The mark alone. Square, and legible down to 16px — below that use the letter
 * without the tile.
 */
export function Logo({
  size = 32,
  tone = "ink",
  className,
}: {
  size?: number;
  tone?: LogoTone;
  className?: string;
}) {
  const c = TONES[tone];
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="MakeMySiteToday"
      focusable="false"
    >
      <rect width="64" height="64" rx="15" fill={c.tile} />
      <g transform="translate(32 32) scale(0.94) translate(-32 -32) translate(0 -3.5)">
        <path d={M} fill={c.letter} />
      </g>
      <path d={KERB} fill={c.kerb} />
    </svg>
  );
}

/** The M glyph alone, in currentColor — for a spinner that must read on ANY
 *  background: it inherits the surrounding text colour (white on a dark button,
 *  ink on a light one), so no per-use tone guessing is ever needed. */
export function LogoLetter({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="MakeMySiteToday"
      focusable="false"
    >
      <g transform="translate(32 32) scale(0.94) translate(-32 -32) translate(0 -3.5)">
        <path d={M} fill="currentColor" />
      </g>
    </svg>
  );
}

/**
 * The mark with the name beside it. The mark may stand alone; the wordmark may
 * not — so the two are only ever set together by this one function.
 */
export function Wordmark({
  suffix,
  size = 30,
  tone = "ink",
  className = "font-serif text-lg font-semibold",
}: {
  /** " · Rep" and the like — the rep's surface says so on every page of it. */
  suffix?: string;
  size?: number;
  tone?: LogoTone;
  className?: string;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <Logo size={size} tone={tone} className="shrink-0" />
      <span className={`truncate ${className}`}>
        MakeMySiteToday
        {suffix ?? ""}
      </span>
    </span>
  );
}
